import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  Download,
  Calendar,
  Phone as PhoneIcon,
  Mail,
  Building2,
  TrendingUp,
  Heart,
  Users,
  Sparkles,
  Target,
  Network,
  BarChart3,
  Workflow,
  HandCoins,
  Globe,
  Activity,
  ShieldCheck
} from 'lucide-react';
import useRevealObserver from '../hooks/useRevealObserver';
import AITestimonyBlock from '../components/AITestimonyBlock';
import { submitLead } from '../lib/api';

/* =========================================================
   CountUp — gentle number animation (no aggressive motion)
   ========================================================= */
const useCountUp = (target, durationMs = 1600, run = false, format = (n) => n) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, run]);
  return format(val);
};

/* =========================================================
   Stat card with intersection-driven CountUp
   ========================================================= */
const StatCard = ({ value, suffix, label, format }) => {
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRun(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const display = useCountUp(value, 1700, run, format || ((n) => n.toLocaleString('en-US')));
  return (
    <div ref={ref} className="text-center">
      <div className="zh-display text-5xl md:text-6xl" style={{ color: '#1a3a5c' }}>
        {display}
        {suffix && <span className="text-3xl md:text-4xl" style={{ color: '#b8975f' }}>{suffix}</span>}
      </div>
      <div className="mt-3 text-sm md:text-base" style={{ color: 'rgba(26,58,92,0.70)' }}>
        {label}
      </div>
    </div>
  );
};

/* =========================================================
   Main Zchuta investor pitch deck page
   ========================================================= */
const Zchuta = () => {
  useRevealObserver();

  // ---- Form state ----
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    investorType: '',
    investmentRange: '',
    wantDeck: 'yes',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const formRef = useRef(null);
  const errorRef = useRef(null);

  const onChange = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'יש להזין שם מלא';
    if (!form.phone.trim()) e.phone = 'יש להזין מספר טלפון';
    if (!form.email.trim()) e.email = 'יש להזין כתובת אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'כתובת אימייל לא תקינה';
    if (!form.investorType) e.investorType = 'יש לבחור סוג משקיע';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      setTimeout(() => errorRef.current?.focus(), 50);
      return;
    }
    setSubmitting(true);
    try {
      const wantDeckText = form.wantDeck === 'yes' ? 'מעוניין ב-Deck מלא' : 'לא מעוניין ב-Deck מלא';
      await submitLead({
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        company: form.investmentRange || 'משקיע פוטנציאלי — זכותא',
        role: `${form.investorType} · ${wantDeckText}`,
        challenge: form.notes || undefined,
        source: 'zchuta-landing'
      });
      setSubmitted(true);
    } catch (err) {
      setServerError(err?.response?.data?.detail || 'שגיאה בשליחת הטופס. נסו שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Static content (per spec) ----
  const PROBLEMS = [
    {
      icon: HandCoins,
      title: 'צורך תמידי בכסף',
      text: 'בתי כנסת, ישיבות, כוללים ועמותות חיים על תרומות לפעילות שוטפת, בנייה, הנצחות וסיוע לקהילה.'
    },
    {
      icon: Users,
      title: 'קושי להגיע לתורמים חדשים',
      text: 'הגיוס נשען על קשרים אישיים ופניות ידניות, ללא מנוע שיווקי שיודע להביא תורמים חדשים בקנה מידה.'
    },
    {
      icon: Activity,
      title: 'מעבר חלש להוראות קבע',
      text: 'תרומות חד־פעמיות לא הופכות להכנסה חוזרת, ולמוסדות אין תהליך מסודר לטיפוח קשר ארוך טווח עם תורמים.'
    }
  ];

  const PRODUCT_FEATURES = [
    { icon: HandCoins, en: 'Donation pages', he: 'דפי תרומה ממירים' },
    { icon: Sparkles, en: 'Campaign pages', he: 'דפי קמפיין מנוהלים' },
    { icon: Activity, en: 'Recurring donations', he: 'הוראות קבע חודשיות' },
    { icon: Heart, en: 'Memorial donations', he: 'הנצחות והקדשות' },
    { icon: Users, en: 'Donor journey', he: 'מסע תורם מובנה' },
    { icon: BarChart3, en: 'Analytics', he: 'דאטה ואופטימיזציה' },
    { icon: Network, en: 'CRM integration', he: 'אינטגרציית CRM' },
    { icon: Target, en: 'Campaign optimization', he: 'אופטימיזציה לתוצאות' }
  ];

  const GTM_POINTS = [
    'קשרים קיימים עם מוסדות, גבאים ורבנים',
    'קהילות דתיות וקהילות חב״ד',
    'שיתופי פעולה עם יועצי עמותות ורואי חשבון',
    'מקרי הצלחה מפיילוטים מבוססי הצלחה',
    'מודל Success-based שמפחית התנגדות כניסה',
    'קמפיינים ממוקדים למנהלי מוסדות'
  ];

  const USE_OF_FUNDS = [
    { label: 'Product & Platform', detail: 'פיתוח ושיפור המוצר, אוטומציות וניהול דאטה' },
    { label: 'First 100 Institutions', detail: 'גיוס והפעלת 100 המוסדות הראשונים' },
    { label: 'Major Campaigns', detail: 'שני קמפיינים גדולים להוכחת המודל' },
    { label: 'Data & CRM Infrastructure', detail: 'תשתיות מעקב, CRM ואופטימיזציה' },
    { label: 'Funnel & Recurring Optimization', detail: 'שיפור משפכי תרומה והוראות קבע' },
    { label: 'International Readiness', detail: 'הכנה לכניסה לקהילות יהודיות בחו״ל' }
  ];

  const MILESTONES = [
    '100 מוסדות פעילים ראשונים',
    '2 קמפיינים גדולים להוכחת יכולת',
    'מדידת CAC, LTV ורווח גולמי',
    'הוכחת יכולת לייצר תרומות חוזרות',
    'תשתית לסקייל בישראל',
    'הכנה לכניסה לקהילות יהודיות בעולם'
  ];

  return (
    <div className="zchuta-page min-h-screen">
      {/* Skip link */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:right-2 focus:bg-white focus:text-navy focus:px-4 focus:py-2 focus:z-[100] focus:border-2 focus:border-[#1a3a5c]"
        data-testid="zchuta-skip-link"
      >
        דלג לתוכן הראשי
      </a>

      {/* ===== Top bar ===== */}
      <header
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(250, 246, 239, 0.85)', borderBottom: '1px solid rgba(184, 151, 95, 0.18)' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <a href="/zchuta" className="flex items-center gap-3" data-testid="zchuta-logo-link">
            <img
              src="/assets/zchuta/logo.png"
              alt="לוגו זכותא"
              className="h-10 w-10 object-contain"
            />
            <div className="leading-tight">
              <div className="zh-display text-xl" style={{ color: '#1a3a5c' }}>זכותא</div>
              <div className="text-[10px]" style={{ color: '#b8975f', letterSpacing: '0.16em' }}>INVESTOR DECK</div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled
              className="zh-btn-disabled hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm rounded-sm"
              title="זמין בקרוב — להורדת המצגת"
              data-testid="zchuta-download-deck-top"
            >
              <Download className="w-4 h-4" aria-hidden="true" />
              <span>להורדת המצגת (בקרוב)</span>
            </button>
            <button
              type="button"
              onClick={scrollToForm}
              className="zh-btn-primary inline-flex items-center gap-2 px-4 md:px-5 py-2.5 text-sm rounded-sm"
              data-testid="zchuta-top-cta"
            >
              <Calendar className="w-4 h-4" aria-hidden="true" />
              <span>לתיאום שיחת משקיעים</span>
            </button>
          </div>
        </div>
      </header>

      <main id="main">
        {/* =================== HERO =================== */}
        <section
          className="relative overflow-hidden"
          style={{ paddingTop: '5rem', paddingBottom: '5rem' }}
          data-testid="zchuta-hero"
        >
          {/* Subtle arch background pattern */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-[0.05]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='160' height='200' viewBox='0 0 160 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M80 0 C 30 0 30 80 30 200 L130 200 C130 80 130 0 80 0 Z' fill='none' stroke='%23b8975f' stroke-width='1.5'/%3E%3C/svg%3E\")",
              backgroundSize: '160px 200px',
              backgroundRepeat: 'repeat'
            }}
          />
          <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
            <div className="reveal text-center">
              <div className="zh-eyebrow mb-5">Investor Pitch · Seed Round</div>
              <h1 className="zh-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: '#1a3a5c' }}>
                זכותא — מנוע גיוס תרומות{' '}
                <span className="zh-gold-underline" style={{ color: '#b8975f' }}>
                  דיגיטלי
                </span>
                <br className="hidden md:block" /> למוסדות וקהילות
              </h1>
              <p className="mt-7 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: 'rgba(26,58,92,0.85)' }}>
                פלטפורמה מבוססת הצלחה שמסייעת למוסדות לגייס תרומות, הנצחות והוראות קבע —
                וגובה אחוז מתוך התרומות שהיא מסייעת לייצר.
              </p>

              {/* Powered by ribbon */}
              <div className="mt-7 inline-flex items-center gap-3 px-4 py-2 rounded-full"
                style={{ background: 'rgba(184, 151, 95, 0.12)', border: '1px solid rgba(184, 151, 95, 0.35)' }}
              >
                <ShieldCheck className="w-4 h-4" style={{ color: '#b8975f' }} aria-hidden="true" />
                <span className="text-sm" style={{ color: '#1a3a5c' }}>
                  Powered by <strong>GAL &amp; BLIZOVSKY</strong> · 400M+ NIS בגיוס תרומות דיגיטלי ב-5 שנים
                </span>
              </div>

              <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="zh-btn-primary inline-flex items-center gap-2 px-7 py-3.5 text-base rounded-sm"
                  data-testid="zchuta-hero-cta-primary"
                >
                  <Calendar className="w-5 h-5" aria-hidden="true" />
                  <span>לתיאום שיחת משקיעים</span>
                </button>
                <button
                  type="button"
                  disabled
                  className="zh-btn-disabled inline-flex items-center gap-2 px-7 py-3.5 text-base rounded-sm"
                  title="זמין בקרוב — להורדת המצגת"
                  data-testid="zchuta-hero-cta-secondary"
                >
                  <Download className="w-5 h-5" aria-hidden="true" />
                  <span>צפייה במצגת (בקרוב)</span>
                </button>
              </div>
            </div>

            {/* Hero stat strip */}
            <div className="reveal mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 max-w-4xl mx-auto"
              style={{ transitionDelay: '120ms' }}
            >
              <StatCard value={400} suffix="M+" label="ש״ח גויסו דיגיטלית" format={(n) => n} />
              <StatCard value={5} suffix=" שנים" label="ניסיון בתחום" format={(n) => n} />
              <StatCard value={15000} suffix="+" label="בתי כנסת בישראל" />
              <StatCard value={250} suffix="" label="מוסדות חדשים בשנה" />
            </div>
            <p className="mt-4 text-xs text-center" style={{ color: 'rgba(26,58,92,0.55)' }}>
              * Market figures are working assumptions and will be validated during the Seed stage.
            </p>
          </div>
        </section>

        <div className="zh-rule" />

        {/* AI TESTIMONY + CRYPTOGRAPHIC VERIFICATION — GEO block */}
        <AITestimonyBlock />

        {/* =================== THE PROBLEM =================== */}
        <section className="py-20 md:py-28" data-testid="zchuta-problem">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">The Problem</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                מוסדות צריכים תרומות קבועות, <br className="hidden md:block" />
                אבל לרוב אין להם מנוע גיוס קבוע.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
              {PROBLEMS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <article
                    key={p.title}
                    className="reveal zh-card p-7 md:p-8 rounded-sm"
                    style={{ transitionDelay: `${i * 90}ms` }}
                    data-testid={`zchuta-problem-card-${i}`}
                  >
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-sm mb-5"
                      style={{ background: 'rgba(184, 151, 95, 0.15)' }}
                    >
                      <Icon className="w-6 h-6" style={{ color: '#b8975f' }} aria-hidden="true" />
                    </div>
                    <h3 className="zh-display text-xl mb-3" style={{ color: '#1a3a5c' }}>{p.title}</h3>
                    <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(26,58,92,0.75)' }}>
                      {p.text}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* =================== MARKET OPPORTUNITY =================== */}
        <section className="py-20 md:py-28" style={{ background: 'rgba(184, 151, 95, 0.05)' }} data-testid="zchuta-market">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-12">
              <div className="zh-eyebrow mb-3">Market Opportunity</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                שוק גדול, חוזר, רגשי — <br className="hidden md:block" />
                ועדיין לא ממומש דיגיטלית.
              </h2>
              <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
                ההזדמנות האמיתית אינה רק מספר המוסדות — אלא העובדה שכל אחד מהם זקוק לגיוס שוב ושוב,
                לאורך זמן, עם רגש חזק ובסיס תורמים נאמן.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { v: '15,000+', l: 'בתי כנסת בישראל' },
                { v: '250', l: 'מוסדות חדשים בשנה' },
                { v: '10M–30M ₪', l: 'עלות בניית מבנה קבע' },
                { v: 'Global', l: 'קהילות יהודיות בחו״ל' }
              ].map((m, i) => (
                <div
                  key={m.l}
                  className="reveal zh-card p-7 rounded-sm text-center"
                  style={{ transitionDelay: `${i * 80}ms` }}
                  data-testid={`zchuta-market-stat-${i}`}
                >
                  <div className="zh-display text-3xl md:text-4xl" style={{ color: '#1a3a5c' }}>{m.v}</div>
                  <div className="mt-3 text-sm" style={{ color: 'rgba(26,58,92,0.70)' }}>{m.l}</div>
                </div>
              ))}
            </div>
            <p className="reveal mt-6 text-center text-xs" style={{ color: 'rgba(26,58,92,0.55)' }}>
              Market figures are working assumptions and will be validated during the Seed stage.
            </p>
          </div>
        </section>

        {/* =================== THE SOLUTION =================== */}
        <section className="py-20 md:py-28" data-testid="zchuta-solution">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">The Solution</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                זכותא הופכת תרומות לתהליך דיגיטלי מנוהל.
              </h2>
            </div>

            {/* Flow diagram — vertical on mobile, horizontal on desktop */}
            <div className="reveal grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4 items-stretch text-center">
              {[
                { t: 'צורך מוסדי', icon: Building2 },
                { t: 'מסר וקמפיין', icon: Sparkles },
                { t: 'דף תרומה / הנצחה', icon: Heart },
                { t: 'תרומה / הוראת קבע', icon: HandCoins },
                { t: 'דאטה ושיפור', icon: BarChart3 },
                { t: 'הכנסה למוסד + עמלת הצלחה', icon: TrendingUp }
              ].map((step, i, arr) => {
                const Icon = step.icon;
                const last = i === arr.length - 1;
                return (
                  <React.Fragment key={step.t}>
                    <div className="zh-step rounded-sm py-5 px-3 flex flex-col items-center gap-2">
                      <Icon className="w-6 h-6" style={{ color: last ? '#b8975f' : '#1a3a5c' }} aria-hidden="true" />
                      <div className={`text-xs md:text-sm font-semibold ${last ? '' : ''}`}
                        style={{ color: last ? '#b8975f' : '#1a3a5c' }}
                      >
                        {step.t}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <p className="reveal mt-10 text-center text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
              זכותא אינה רק עמוד תרומה — היא מערכת שמחברת מסר, קמפיין, דף תרומה, הנצחה, הוראת קבע,
              מעקב ודאטה למסע אחד שמייצר תרומות בפועל.
            </p>
          </div>
        </section>

        {/* =================== BUSINESS MODEL =================== */}
        <section className="py-20 md:py-28" style={{ background: '#1a3a5c', color: '#faf6ef' }} data-testid="zchuta-business-model">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3" style={{ color: '#dfcba5' }}>Business Model</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#faf6ef' }}>
                Success-based revenue model.
              </h2>
              <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(250,246,239,0.80)' }}>
                המוסד לא נדרש לשלם על מערכת יקרה מראש. זכותא מרוויחה מתוך התרומות שהיא מסייעת לגייס —
                יישור אינטרסים מלא עם המוסד.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              {[
                { t: 'No setup barrier', d: 'ללא חסם כניסה משמעותי למוסד' },
                { t: 'Commission from donations', d: 'עמלה מתוך תרומות שנוצרות דרך המערכת' },
                { t: 'Recurring upside', d: 'פוטנציאל הכנסה חוזרת מהוראות קבע' },
                { t: 'Aligned interests', d: 'יישור אינטרסים מלא בין זכותא למוסד' }
              ].map((c, i) => (
                <div
                  key={c.t}
                  className="reveal rounded-sm p-7 h-full"
                  style={{
                    background: 'rgba(250,246,239,0.06)',
                    border: '1px solid rgba(223,203,165,0.25)',
                    transitionDelay: `${i * 80}ms`
                  }}
                  data-testid={`zchuta-bm-card-${i}`}
                >
                  <div className="zh-display text-lg md:text-xl" style={{ color: '#dfcba5' }}>{c.t}</div>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: 'rgba(250,246,239,0.80)' }}>{c.d}</p>
                </div>
              ))}
            </div>

            {/* Simple equation */}
            <div className="reveal mt-12 max-w-3xl mx-auto rounded-sm p-7 md:p-9 text-center"
              style={{ background: 'rgba(184,151,95,0.18)', border: '1px solid rgba(223,203,165,0.40)' }}
            >
              <div className="text-sm zh-eyebrow mb-3" style={{ color: '#dfcba5' }}>Revenue formula</div>
              <div className="zh-display text-2xl md:text-3xl flex items-center justify-center gap-3 flex-wrap" style={{ color: '#faf6ef' }}>
                <span>תרומות שגויסו</span>
                <span style={{ color: '#dfcba5' }}>×</span>
                <span>אחוז עמלה</span>
                <span style={{ color: '#dfcba5' }}>=</span>
                <span>הכנסה לזכותא</span>
              </div>
            </div>
          </div>
        </section>

        {/* =================== WHY WE CAN WIN =================== */}
        <section className="py-20 md:py-28" data-testid="zchuta-why-win">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Why We Can Win</div>
              <h2 className="zh-display text-3xl md:text-5xl leading-tight" style={{ color: '#1a3a5c' }}>
                The fundraising engine <span style={{ color: '#b8975f' }}>already exists.</span>
                <br className="hidden md:block" /> Zchuta turns it into software.
              </h2>
            </div>

            <div className="reveal zh-card rounded-sm p-8 md:p-12 max-w-4xl mx-auto" style={{ background: 'linear-gradient(180deg,#ffffff 0%, #faf6ef 100%)' }}>
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="zh-display text-2xl md:text-3xl tracking-wide" style={{ color: '#1a3a5c' }}>
                  GAL &amp; BLIZOVSKY
                </div>
                <span className="text-xs px-3 py-1 rounded-full"
                  style={{ background: 'rgba(184,151,95,0.15)', color: '#b8975f', letterSpacing: '0.10em' }}
                >
                  לשעבר ב. אסטרטגיה
                </span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-base">
                {[
                  'Boutique growth company',
                  '400M+ NIS in digital donations over 5 years',
                  'Experts in donation funnels & campaigns',
                  'Recurring giving methodology',
                  'Messaging for non-profit audiences',
                  'Teaching & training growth methods'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3" style={{ color: 'rgba(26,58,92,0.85)' }}>
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#b8975f' }} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="zh-rule my-7" />
              <p className="text-center text-base md:text-lg" style={{ color: 'rgba(26,58,92,0.80)' }}>
                זכותא היא <strong>מוצריזציה</strong> של יכולת קיימת להביא כסף.
                הטכנולוגיה היא שכבת הסקייל — היכולת להביא תרומות היא המנוע.
              </p>
            </div>
          </div>
        </section>

        {/* =================== PRODUCT =================== */}
        <section className="py-20 md:py-28" style={{ background: 'rgba(184, 151, 95, 0.05)' }} data-testid="zchuta-product">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Product</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                The platform.
              </h2>
              <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
                שמונה רכיבים שעובדים יחד כדי להפוך צורך אמיתי של מוסד להכנסה דיגיטלית מנוהלת.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {PRODUCT_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.en}
                    className="reveal zh-card rounded-sm p-6 flex flex-col items-start gap-3"
                    style={{ transitionDelay: `${i * 60}ms` }}
                    data-testid={`zchuta-product-${i}`}
                  >
                    <div
                      className="w-11 h-11 flex items-center justify-center rounded-sm"
                      style={{ background: 'rgba(26,58,92,0.08)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: '#1a3a5c' }} aria-hidden="true" />
                    </div>
                    <div>
                      <div className="zh-display text-base md:text-lg" style={{ color: '#1a3a5c' }}>{f.en}</div>
                      <div className="text-sm mt-1" style={{ color: 'rgba(26,58,92,0.65)' }}>{f.he}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =================== GO TO MARKET =================== */}
        <section className="py-20 md:py-28" data-testid="zchuta-gtm">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Go To Market</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                First 100 institutions.
              </h2>
              <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
                החדירה לשוק תתבסס על שילוב בין קשרים, מומחיות שיווקית, והצעה שמפחיתה התנגדות כניסה.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {GTM_POINTS.map((p, i) => (
                <div
                  key={p}
                  className="reveal zh-card rounded-sm p-5 flex items-start gap-3"
                  style={{ transitionDelay: `${i * 60}ms` }}
                  data-testid={`zchuta-gtm-${i}`}
                >
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs zh-display"
                    style={{ background: 'rgba(184,151,95,0.20)', color: '#b8975f' }}
                  >
                    {i + 1}
                  </div>
                  <div className="text-base" style={{ color: 'rgba(26,58,92,0.85)' }}>{p}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =================== UNIT ECONOMICS =================== */}
        <section className="py-20 md:py-28" style={{ background: 'rgba(184, 151, 95, 0.05)' }} data-testid="zchuta-unit-econ">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Unit Economics</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                Built for recurring upside.
              </h2>
              <p className="mt-5 text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
                כל מוסד פעיל אינו לקוח חד־פעמי. הוא נכס הכנסה חוזר שיכול לגדול בכל קמפיין, הנצחה והוראת קבע.
              </p>
            </div>

            <div className="reveal max-w-4xl mx-auto zh-card rounded-sm overflow-hidden">
              <table className="w-full text-right">
                <thead>
                  <tr style={{ background: '#1a3a5c', color: '#faf6ef' }}>
                    <th className="zh-display text-sm md:text-base px-5 py-4 font-medium">מדד</th>
                    <th className="zh-display text-sm md:text-base px-5 py-4 font-medium">דוגמה / Working Assumption</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Donation volume per institution', 'X ש״ח / חודש'],
                    ['Commission rate', 'Y % (תיקוף בשלב Seed)'],
                    ['Monthly revenue per institution', 'X × Y%'],
                    ['100 institutions', 'הכנסה חודשית צפויה'],
                    ['1,000 institutions', 'פוטנציאל סקייל משמעותי'],
                    ['Gross margin', 'לאחר תפעול ומדיה']
                  ].map(([k, v], i) => (
                    <tr key={k} style={{ borderTop: i === 0 ? 'none' : '1px solid rgba(26,58,92,0.08)' }}>
                      <td className="px-5 py-4 text-sm md:text-base" style={{ color: '#1a3a5c' }}>{k}</td>
                      <td className="px-5 py-4 text-sm md:text-base" style={{ color: 'rgba(26,58,92,0.70)' }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="reveal mt-5 text-center text-xs" style={{ color: 'rgba(26,58,92,0.55)' }}>
              * המספרים הסופיים יוצגו לאחר תיקוף Seed. השדות מוגדרים כדינמיים בקוד וניתנים לעדכון.
            </p>
          </div>
        </section>

        {/* =================== TEAM =================== */}
        <section className="py-20 md:py-28" data-testid="zchuta-team">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Team</div>
              <h2 className="zh-display text-3xl md:text-5xl" style={{ color: '#1a3a5c' }}>
                Built by operators who already <br className="hidden md:block" />
                know how to generate donations.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                {
                  name: 'ציון גל',
                  role: 'מייסד גל מהנדסים · ניהול ותפעול',
                  text: 'מהנדס בכיר עם ניסיון רחב בניהול פרויקטים מורכבים, תשתיות, תהליכים ובקרה. מביא לזכותא יכולת ניהול, סדר, תכנון וביצוע בסטנדרט גבוה.'
                },
                {
                  name: 'רז בליזובסקי',
                  role: 'אסטרטגיית גיוס תרומות דיגיטלי',
                  text: 'מומחה לאסטרטגיות שיווק דיגיטלי, קמפיינים, גיוס תרומות אונליין ומשפכי המרה לעמותות. הוביל מהלכים שהכניסו מעל 400 מיליון ש״ח לעמותות בישראל ב-5 השנים האחרונות.'
                }
              ].map((m, i) => (
                <article
                  key={m.name}
                  className="reveal zh-card rounded-sm p-8"
                  style={{ transitionDelay: `${i * 100}ms` }}
                  data-testid={`zchuta-team-${i}`}
                >
                  <div className="zh-display text-2xl md:text-3xl" style={{ color: '#1a3a5c' }}>{m.name}</div>
                  <div className="text-sm mt-1 mb-4" style={{ color: '#b8975f', letterSpacing: '0.08em' }}>{m.role}</div>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(26,58,92,0.78)' }}>{m.text}</p>
                </article>
              ))}
            </div>
            <p className="reveal mt-10 text-center text-base md:text-lg max-w-3xl mx-auto" style={{ color: 'rgba(26,58,92,0.75)' }}>
              המסר: ביצוע + שיווק + עולם מוסדות + יכולת להביא כסף.
            </p>
          </div>
        </section>

        {/* =================== THE ASK =================== */}
        <section className="py-20 md:py-28" style={{ background: '#1a3a5c', color: '#faf6ef' }} data-testid="zchuta-ask">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-12">
              <div className="zh-eyebrow mb-3" style={{ color: '#dfcba5' }}>The Ask</div>
              <h2 className="zh-display text-3xl md:text-5xl leading-tight" style={{ color: '#faf6ef' }}>
                Raising <span style={{ color: '#dfcba5' }}>$180,000</span><br className="hidden md:block" />
                to validate and scale the first 100 institutions.
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Use of funds */}
              <div className="reveal">
                <div className="zh-display text-lg md:text-xl mb-5" style={{ color: '#dfcba5' }}>Use of funds</div>
                <ul className="space-y-4">
                  {USE_OF_FUNDS.map((u, i) => (
                    <li key={u.label} className="flex items-start gap-4 pb-4"
                      style={{ borderBottom: i === USE_OF_FUNDS.length - 1 ? 'none' : '1px solid rgba(223,203,165,0.18)' }}
                      data-testid={`zchuta-funds-${i}`}
                    >
                      <div className="w-9 h-9 flex-shrink-0 rounded-sm flex items-center justify-center text-sm zh-display"
                        style={{ background: 'rgba(223,203,165,0.18)', color: '#dfcba5' }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div>
                        <div className="zh-display text-base md:text-lg" style={{ color: '#faf6ef' }}>{u.label}</div>
                        <div className="text-sm" style={{ color: 'rgba(250,246,239,0.72)' }}>{u.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Milestones */}
              <div className="reveal" style={{ transitionDelay: '120ms' }}>
                <div className="zh-display text-lg md:text-xl mb-5" style={{ color: '#dfcba5' }}>Milestones</div>
                <ul className="space-y-4">
                  {MILESTONES.map((m, i) => (
                    <li key={m} className="flex items-start gap-3" data-testid={`zchuta-milestone-${i}`}>
                      <Target className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#dfcba5' }} aria-hidden="true" />
                      <span className="text-sm md:text-base" style={{ color: 'rgba(250,246,239,0.85)' }}>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* =================== CLOSING + FORM =================== */}
        <section ref={formRef} className="py-20 md:py-28" data-testid="zchuta-closing">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="reveal text-center mb-14">
              <div className="zh-eyebrow mb-3">Closing</div>
              <h2 className="zh-display text-3xl md:text-5xl leading-tight" style={{ color: '#1a3a5c' }}>
                Zchuta is not another donation platform. <br className="hidden md:block" />
                It is a <span style={{ color: '#b8975f' }}>success-based fundraising engine</span> for institutions.
              </h2>
            </div>

            {/* Investor form */}
            <div className="reveal zh-form-panel rounded-sm p-7 md:p-12 max-w-3xl mx-auto">
              {submitted ? (
                <div className="text-center py-8" data-testid="zchuta-form-success">
                  <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5"
                    style={{ background: 'rgba(184,151,95,0.18)' }}
                  >
                    <Check className="w-7 h-7" style={{ color: '#b8975f' }} aria-hidden="true" />
                  </div>
                  <h3 className="zh-display text-2xl md:text-3xl mb-3" style={{ color: '#1a3a5c' }}>תודה, קיבלנו את הפרטים.</h3>
                  <p className="text-base md:text-lg" style={{ color: 'rgba(26,58,92,0.75)' }}>
                    נחזור אליכם לתיאום שיחת משקיעים קצרה.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate aria-labelledby="zh-form-title" data-testid="zchuta-investor-form">
                  <div className="text-center mb-7">
                    <div className="zh-eyebrow mb-2">Investor Form</div>
                    <h3 id="zh-form-title" className="zh-display text-2xl md:text-3xl" style={{ color: '#1a3a5c' }}>
                      לתיאום שיחת משקיעים
                    </h3>
                    <p className="mt-2 text-sm md:text-base" style={{ color: 'rgba(26,58,92,0.70)' }}>
                      נחזור אליכם תוך 24 שעות עם פרטים נוספים ואפשרות לקבל את ה-Deck המלא.
                    </p>
                  </div>

                  {/* Error summary */}
                  {Object.keys(errors).length > 0 && (
                    <div
                      ref={errorRef}
                      tabIndex={-1}
                      role="alert"
                      className="mb-5 p-4 rounded-sm flex items-start gap-3"
                      style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.40)' }}
                      data-testid="zchuta-form-errors"
                    >
                      <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-600" aria-hidden="true" />
                      <div>
                        <div className="font-semibold text-red-700">יש לתקן את השדות הבאים:</div>
                        <ul className="text-sm text-red-700 mt-1 list-disc pr-5">
                          {Object.entries(errors).map(([field, message]) => (
                            <li key={field}>{message}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {serverError && (
                    <div className="mb-5 p-4 rounded-sm text-sm"
                      style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.40)', color: '#b91c1c' }}
                      role="alert"
                    >
                      {serverError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Full name */}
                    <div>
                      <label htmlFor="zh-fullName" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        שם מלא <span style={{ color: '#b8975f' }}>*</span>
                      </label>
                      <input
                        id="zh-fullName"
                        name="fullName"
                        type="text"
                        value={form.fullName}
                        onChange={onChange('fullName')}
                        className="zh-input w-full px-4 py-3 rounded-sm"
                        autoComplete="name"
                        aria-invalid={!!errors.fullName}
                        aria-describedby={errors.fullName ? 'zh-fullName-err' : undefined}
                        data-testid="zchuta-input-fullName"
                      />
                      {errors.fullName && <div id="zh-fullName-err" className="text-xs text-red-600 mt-1">{errors.fullName}</div>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="zh-phone" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        טלפון <span style={{ color: '#b8975f' }}>*</span>
                      </label>
                      <input
                        id="zh-phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={onChange('phone')}
                        className="zh-input w-full px-4 py-3 rounded-sm"
                        autoComplete="tel"
                        dir="ltr"
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'zh-phone-err' : undefined}
                        data-testid="zchuta-input-phone"
                      />
                      {errors.phone && <div id="zh-phone-err" className="text-xs text-red-600 mt-1">{errors.phone}</div>}
                    </div>

                    {/* Email */}
                    <div className="md:col-span-2">
                      <label htmlFor="zh-email" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        דוא״ל <span style={{ color: '#b8975f' }}>*</span>
                      </label>
                      <input
                        id="zh-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange('email')}
                        className="zh-input w-full px-4 py-3 rounded-sm"
                        autoComplete="email"
                        dir="ltr"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'zh-email-err' : undefined}
                        data-testid="zchuta-input-email"
                      />
                      {errors.email && <div id="zh-email-err" className="text-xs text-red-600 mt-1">{errors.email}</div>}
                    </div>

                    {/* Investor type */}
                    <div>
                      <label htmlFor="zh-investorType" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        סוג משקיע <span style={{ color: '#b8975f' }}>*</span>
                      </label>
                      <select
                        id="zh-investorType"
                        name="investorType"
                        value={form.investorType}
                        onChange={onChange('investorType')}
                        className="zh-input w-full px-4 py-3 rounded-sm"
                        aria-invalid={!!errors.investorType}
                        aria-describedby={errors.investorType ? 'zh-investorType-err' : undefined}
                        data-testid="zchuta-input-investorType"
                      >
                        <option value="">בחרו אפשרות</option>
                        <option value="משקיע פרטי">משקיע פרטי</option>
                        <option value="אנג׳ל">אנג׳ל</option>
                        <option value="קרן Seed">קרן Seed</option>
                        <option value="שותף אסטרטגי">שותף אסטרטגי</option>
                      </select>
                      {errors.investorType && <div id="zh-investorType-err" className="text-xs text-red-600 mt-1">{errors.investorType}</div>}
                    </div>

                    {/* Investment range */}
                    <div>
                      <label htmlFor="zh-range" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        סכום השקעה משוער
                      </label>
                      <select
                        id="zh-range"
                        name="investmentRange"
                        value={form.investmentRange}
                        onChange={onChange('investmentRange')}
                        className="zh-input w-full px-4 py-3 rounded-sm"
                        data-testid="zchuta-input-range"
                      >
                        <option value="">בחרו אפשרות</option>
                        <option value="עד 25K$">עד 25,000$</option>
                        <option value="25K–50K$">25,000–50,000$</option>
                        <option value="50K–100K$">50,000–100,000$</option>
                        <option value="100K$ ומעלה">100,000$ ומעלה</option>
                      </select>
                    </div>

                    {/* Want full deck */}
                    <div className="md:col-span-2">
                      <fieldset>
                        <legend className="block text-sm font-semibold mb-2" style={{ color: '#1a3a5c' }}>
                          האם תרצו לקבל את ה-Deck המלא?
                        </legend>
                        <div className="flex items-center gap-5">
                          {[
                            { v: 'yes', l: 'כן, אשלחו לי את ה-Deck' },
                            { v: 'no', l: 'לא, רק שיחה ראשונית' }
                          ].map((o) => (
                            <label key={o.v} className="flex items-center gap-2 cursor-pointer" style={{ color: 'rgba(26,58,92,0.85)' }}>
                              <input
                                type="radio"
                                name="wantDeck"
                                value={o.v}
                                checked={form.wantDeck === o.v}
                                onChange={onChange('wantDeck')}
                                className="w-4 h-4"
                                style={{ accentColor: '#b8975f' }}
                                data-testid={`zchuta-input-wantDeck-${o.v}`}
                              />
                              <span className="text-sm">{o.l}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    </div>

                    {/* Notes */}
                    <div className="md:col-span-2">
                      <label htmlFor="zh-notes" className="block text-sm font-semibold mb-1.5" style={{ color: '#1a3a5c' }}>
                        הודעה חופשית
                      </label>
                      <textarea
                        id="zh-notes"
                        name="notes"
                        value={form.notes}
                        onChange={onChange('notes')}
                        rows={4}
                        placeholder="מה היית רוצה לדעת לפני השיחה?"
                        className="zh-input w-full px-4 py-3 rounded-sm resize-y"
                        data-testid="zchuta-input-notes"
                      />
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col sm:flex-row gap-3 items-center justify-center">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="zh-btn-primary inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-sm w-full sm:w-auto"
                      data-testid="zchuta-form-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                          <span>שולח…</span>
                        </>
                      ) : (
                        <>
                          <Calendar className="w-5 h-5" aria-hidden="true" />
                          <span>לתיאום שיחת משקיעים</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      disabled
                      className="zh-btn-disabled inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-sm w-full sm:w-auto"
                      title="זמין בקרוב — להורדת המצגת"
                      data-testid="zchuta-form-download"
                    >
                      <Download className="w-5 h-5" aria-hidden="true" />
                      <span>להורדת המצגת (בקרוב)</span>
                    </button>
                  </div>

                  <p className="mt-5 text-center text-xs" style={{ color: 'rgba(26,58,92,0.55)' }}>
                    הפרטים נשמרים אצלנו בלבד לצרכי תיאום פגישת משקיעים. לא ישותפו עם צד שלישי.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10" style={{ borderTop: '1px solid rgba(184,151,95,0.20)' }}>
          <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/assets/zchuta/logo.png" alt="" aria-hidden="true" className="h-8 w-8 object-contain" />
              <div className="text-sm" style={{ color: 'rgba(26,58,92,0.65)' }}>
                © {new Date().getFullYear()} זכותא · Powered by GAL &amp; BLIZOVSKY
              </div>
            </div>
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm hover:underline"
              style={{ color: '#1a3a5c' }}
              data-testid="zchuta-back-home"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span>חזרה לאתר Gal &amp; Blizovsky</span>
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Zchuta;
